import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { Checkbox } from '../../../ui/checkbox';
import { JobDetailsDialog } from '../../shared/JobDetailsDialog';
import { Calendar, Clock, DollarSign, MapPin, RefreshCw, CheckCircle, XCircle, Clock1, Plus, CreditCard, Wrench, Eye, Clock11 } from 'lucide-react';
import { NoJobs } from './NoJobs';
import { useSelector } from 'react-redux';
import { getHomeownerJobs } from '../../../../apiManager/houseOwner/yourjobs';
import { Loader } from '../../../../components/base/Loader'
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate, formatPayment, formatTime, getStatusIcon, getTimeRemaining, isScheduled } from '../../../../lib/utils';
import { getStatusBadge } from './detail/StatusUtil';
import { BACKEND_STRIPE_KEY, BACKEND_STRIPE_URL } from '../../../../config';
import { toast } from 'sonner';
import api from '../../../../apiManager/api';

const stripePromise = loadStripe(BACKEND_STRIPE_KEY);
const baseUrl = BACKEND_STRIPE_URL;

export function JobsTab() {
  const { userInfo } = useSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedJobType, setSelectedJobType] = useState(searchParams.get('type') || 'paid');
  const [selectedUnpaidJobs, setSelectedUnpaidJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [paidJobs, setPaidJobs] = useState([]);
  const [unpaidJobs, setUnpaidJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const navigate = useNavigate();

  const token = userInfo ? userInfo.token : null;

  // Check payment status on component mount
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const canceledParam = urlParams.get('canceled');

      if (canceledParam) {
        toast.error('Payment was cancelled');
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      if (sessionId) {
        try {
          const response = await fetch(`${baseUrl}/verifyStatus`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ sessionId }),
          });

          if (!response.ok) {
            throw new Error('Failed to fetch payment status');
          }

          toast.success('Payment completed successfully!');
        } catch (err) {
          console.error('Error fetching payment status:', err);
          toast.error('An error occurred while checking payment status.');
        } finally {
          // Clean up URL
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
    };

    if (token) {
      checkPaymentStatus();
    }
  }, [token, selectedJobType]);

  // Sync URL with selected tab
  useEffect(() => {
    const currentType = searchParams.get('type') || 'paid';
    setSelectedJobType(currentType);
  }, [searchParams]);

  // Update URL when tab changes
  const handleTabChange = (type) => {
    setSelectedJobType(type);
    setSearchParams({ type });
  };

  useEffect(() => {
    fetchJobs(selectedJobType);
  }, [selectedJobType]);

  const fetchJobs = async (type) => {
    if (!userInfo) return;

    setLoading(true);
    try {
      const jobs = await getHomeownerJobs(type);
      if (type === 'paid') {
        setPaidJobs(jobs);
      } else {
        setUnpaidJobs(jobs);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpaidJobSelection = (jobId, checked) => {
    if (checked) {
      setSelectedUnpaidJobs([...selectedUnpaidJobs, jobId]);
    } else {
      setSelectedUnpaidJobs(selectedUnpaidJobs.filter(id => id !== jobId));
    }
  };

  const handleConfirmPayment = async () => {
    if (selectedUnpaidJobs.length === 0) {
      toast.error('Please select at least one job to pay for');
      return;
    }

    setCheckoutLoading(true);
    try {
      // Get the selected jobs data
      const selectedJobsData = unpaidJobs.filter(job => selectedUnpaidJobs.includes(job._id));
      const params = selectedJobsData.length === 1 ? 'type=single&capture=manual' : 'type=multiple&capture=instant';
      const response = await api.post(`/stripe/checkout?${params}`, {
          uri: '/homeowner/jobs',
          jobs: selectedJobsData.map(job => ({ amount: job.paymentInfo.amount, jobId: job._id }))
      });

      console.log(response);

      const { id } = await response.data;
      const stripe = await stripePromise;

      const { error } = await stripe.redirectToCheckout({ sessionId: id });

      if (error) {
        console.error('Error redirecting to checkout:', error);
        toast.error(`Payment failed: ${error.message}`);
      }
    } catch (error) {
      console.error('Error handling checkout:', error);
      toast.error(`Payment error: ${error.message}`);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleRequestRefund = (jobId) => {
    console.log('Requesting refund for job:', jobId);
    toast.info('Refund request feature coming soon');
  };

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setShowJobDetails(true);
  };

  const EmptyState = ({ type }) => (
    <div className="text-center py-16 space-y-6">
      <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
        {type === 'paid' ? (
          <CreditCard className="w-12 h-12 text-gray-400" />
        ) : (
          <Plus className="w-12 h-12 text-gray-400" />
        )}
      </div>

      <div className="space-y-3">
        <h3 className="text-2xl font-semibold text-gray-900">
          {type === 'paid' ? 'No Paid Jobs Yet' : 'No Unpaid Jobs Yet'}
        </h3>
        <p className="text-gray-600 max-w-md mx-auto text-lg">
          {type === 'paid'
            ? "You haven't completed any paid jobs yet. Get started by posting a new job or authorizing unpaid jobs."
            : "You haven't posted any jobs yet. Create your first job posting to get started."
          }
        </p>
      </div>

      <div className="flex justify-center gap-4 pt-4">
        {type === 'paid' ? (
          <>
            <Button
              size="lg"
              className="px-8 flex items-center gap-2"
              onClick={() => navigate('/homeowner/post')}
            >
              <Plus className="w-5 h-5" />
              Post New Job
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="px-8 flex items-center gap-2"
              onClick={() => navigate('/homeowner/jobs?type=unpaid')}
            >
              <CreditCard className="w-5 h-5" />
              Authorize Unpaid Jobs
            </Button>
          </>
        ) : (
          <Button
            size="lg"
            className="px-8 flex items-center gap-2"
            onClick={() => navigate('/homeowner/post')}
          >
            <Plus className="w-5 h-5" />
            Post Your First Job
          </Button>
        )}
      </div>
    </div>
  );

  if (!userInfo) {
    return <NoJobs />;
  }

  const renderJobCard = (job, isPaid = true) => {
    const wasScheduled = isScheduled(job.createdAt, job.scheduledDate);
    const postTimeRemaining = wasScheduled ? getTimeRemaining(job.scheduledDate, job.scheduledTime) : null;

    return (
      <Card key={job._id} className="hover:shadow-xl transition-all duration-300 hover:scale-[1.02] border-l-4 border-l-blue-500">
        <CardContent className="p-6">
          {/* Header Section */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3 flex-1">
              {!isPaid && job.jobStatus === 'open' && (
                <Checkbox
                  checked={selectedUnpaidJobs.includes(job._id)}
                  onCheckedChange={(checked) => handleUnpaidJobSelection(job._id, checked)}
                  className="w-5 h-5 mt-1"
                />
              )}
              {isPaid && (
                <div className="mt-1">
                  {getStatusIcon(job.jobStatus)}
                </div>
              )}

              <div className="flex-1">
                <div className="flex justify-between items-center gap-3 mb-2">
                  <h3 className="text-xl font-bold text-gray-900">
                    {job.serviceDetail?.service}
                  </h3>
                  {isPaid && getStatusBadge(job.jobStatus)}
                </div>

                <div className="flex justify-between items-center gap-2 text-sm text-gray-600">
                  <div className='flex items-center gap-2'>
                    <Calendar className="w-4 h-4" />
                    <span>Posted: {formatDate(job.createdAt)}</span>
                  </div>
                  {job.jobStatus === 'scheduled' && postTimeRemaining && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full ml-2">
                      <Calendar className="w-3 h-3" />
                      Around {postTimeRemaining.days}d, {postTimeRemaining.hours}h and {postTimeRemaining.minutes}m remaining
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Information Rows - Single Column */}
          <div className="space-y-3 mb-6">
            {/* Scheduled Time */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-gray-700">Scheduled Time:</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {formatTime(job.scheduledTime)}
              </span>
            </div>

            {/* Amount */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="w-4 h-4 text-green-600" />
                <span className="font-medium text-gray-700">Amount:</span>
              </div>
              <span className="text-sm font-bold text-green-700">
                ${formatPayment(job.paymentInfo?.amount, false)}
              </span>
            </div>

            {/* Duration */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-gray-700">Duration:</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {job.completionTime}
              </span>
            </div>

            {/* Service Type */}
            {job.serviceType && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <Wrench className="w-4 h-4 text-orange-600" />
                  <span className="font-medium text-gray-700">Service Type:</span>
                </div>
                <span className="text-sm font-semibold text-gray-900 capitalize">
                  {job.serviceType}
                </span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              size="lg"
              className="text-sm flex-1 border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/homeowner/jobs/${job._id}`)
              }}
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </Button>

            {isPaid && job.jobStatus === 'in-progress' && (
              <Button
                variant="outline"
                size="lg"
                className="text-sm flex-1 border-red-600 text-red-600 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestRefund(job._id);
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Request Refund
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-5xl mx-auto space-y-12 py-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-5xl font-bold text-black">Your Jobs</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Manage and track all your service requests
        </p>
      </div>

      {/* Job Type Selector */}
      <div className="flex justify-center">
        <Tabs value={selectedJobType} onValueChange={handleTabChange} className="w-full max-w-lg">
          <TabsList className="grid w-full grid-cols-2 bg-white shadow-lg border p-2 rounded-xl h-16">
            <TabsTrigger
              value="paid"
              className="data-[state=active]:bg-black data-[state=active]:text-white text-lg font-medium h-full transition-all duration-200"
            >
              Paid Jobs
            </TabsTrigger>
            <TabsTrigger
              value="unpaid"
              className="data-[state=active]:bg-black data-[state=active]:text-white text-lg font-medium h-full transition-all duration-200"
            >
              Unpaid Jobs
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Jobs Content */}
      <Tabs value={selectedJobType} onValueChange={handleTabChange}>
        <TabsContent value="paid" className="space-y-4">
          {loading ? (
            <Loader />
          ) : paidJobs.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {paidJobs.map(job => renderJobCard(job, true))}
            </div>
          ) : (
            <EmptyState type="paid" />
          )}
        </TabsContent>

        <TabsContent value="unpaid" className="space-y-6">
          {loading ? (
            <Loader />
          ) : unpaidJobs.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                {unpaidJobs.map(job => renderJobCard(job, false))}
              </div>

              {/* Confirm Payment Button for Unpaid Jobs */}
              {selectedUnpaidJobs.length > 0 && (
                <div className="flex justify-center pt-6">
                  <Card className="p-6 bg-black text-white">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-lg">
                          {selectedUnpaidJobs.length} job{selectedUnpaidJobs.length > 1 ? 's' : ''} selected
                        </p>
                        <p className="text-sm text-gray-300">
                          Total: ${formatPayment(
                            unpaidJobs
                              .filter(job => selectedUnpaidJobs.includes(job._id))
                              .reduce((total, job) => total + (job.paymentInfo?.amount || 0), 0),
                            false
                          )}
                        </p>
                      </div>
                      <Button
                        size="lg"
                        variant="secondary"
                        onClick={handleConfirmPayment}
                        disabled={checkoutLoading}
                        className="bg-white text-black hover:bg-gray-100 flex items-center gap-2"
                      >
                        {checkoutLoading ? (
                          <>
                            <Loader size="w-4 h-4" fScreen='' />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4" />
                            Confirm Payment
                          </>
                        )}
                      </Button>
                    </div>
                  </Card>
                </div>
              )}
            </>
          ) : (
            <EmptyState type="unpaid" />
          )}
        </TabsContent>
      </Tabs>

      {/* Schedule New Job Button - Only show when there are jobs */}
      {(paidJobs.length > 0 || unpaidJobs.length > 0) && selectedUnpaidJobs.length === 0 && (
        <div className="flex justify-center pt-6">
          <Button
            variant="default"
            size="lg"
            className="px-8 flex items-center gap-2"
            onClick={() => navigate('/homeowner/post')}
          >
            <Clock1 className="w-5 h-5" />
            Schedule New Job
          </Button>
        </div>
      )}

      {/* Job Details Dialog */}
      <JobDetailsDialog
        job={selectedJob}
        isOpen={showJobDetails}
        onClose={() => {
          setShowJobDetails(false);
          setSelectedJob(null);
        }}
        userRole="homeowner"
      />
    </div>
  );
}